import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaArrowLeft, FaTrash } from "react-icons/fa";

import { EmptyState } from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { SchoolOpsHeader } from "context/school-ops-header-context";
import { useAuth } from "context/auth-context";
import { useSchoolPlayerIdentity } from "pages/app/workspace/school-player-profile/hooks/use-school-player-identity";
import {
  PLAYER_PROFILE_TABS,
  PlayerProfileTab,
  parsePlayerProfileTab,
} from "pages/app/workspace/school-player-profile/school-player-profile.types";
import { SchoolPlayerDocumentsTab } from "pages/app/workspace/school-player-profile/tabs/documents-tab";
import { SchoolPlayerMediaTab } from "pages/app/workspace/school-player-profile/tabs/media-tab";
import { SchoolPlayerOverviewTab } from "pages/app/workspace/school-player-profile/tabs/overview-tab";
import { SchoolPlayerPerformanceTab } from "pages/app/workspace/school-player-profile/tabs/performance-tab";
import { SchoolPlayerScheduleTab } from "pages/app/workspace/school-player-profile/tabs/schedule-tab";
import {
  SchoolPlayerOverviewTabSkeleton,
  SchoolPlayerPerformanceTabSkeleton,
  SchoolPlayerProfileTabSkeleton,
} from "pages/app/workspace/school-player-profile.skeleton";
import { workspaceApi } from "pages/app/workspace/services";
import {
  SchoolHubContentStack,
  SchoolHubTabButton,
  SchoolHubTabRow,
} from "pages/app/workspace/styles/school-hub.page.inline.styles";
import {
  SchoolPlayerProfileDangerButton,
  SchoolPlayerProfileSecondaryButton,
  SchoolPlayerProfileSection,
} from "pages/app/workspace/styles/school-player-profile.page.inline.styles";
import { getApiErrorMessage } from "utils/admin";
import { hasAnyPortalAction, isFanPortalAccess } from "utils/portal-access";

export type { PlayerProfileTab } from "pages/app/workspace/school-player-profile/school-player-profile.types";

const SchoolPlayerProfilePage: React.FC = () => {
  const { schoolId, id } = useParams<{
    schoolId: string;
    id: string;
  }>();
  const history = useHistory();
  const location = useLocation<{
    backTo?: string;
  }>();
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();
  const {
    athlete,
    athleteLoading,
    hasProfile,
    isLoading,
    linkedSports,
    linkedTeams,
    profileTitle,
    schoolPlayer,
  } = useSchoolPlayerIdentity(schoolId, id);

  const activeTab = useMemo(() => parsePlayerProfileTab(location.search), [location.search]);
  const backTo = location.state?.backTo || "/workspace/school-players";

  const loadingSkeleton = useMemo(() => {
    if (activeTab === "overview") {
      return <SchoolPlayerOverviewTabSkeleton />;
    }

    if (activeTab === "performance") {
      return <SchoolPlayerPerformanceTabSkeleton />;
    }

    return <SchoolPlayerProfileTabSkeleton />;
  }, [activeTab]);

  const canDeletePlayer =
    !isFanPortalAccess(portalAccess) &&
    hasAnyPortalAction(portalAccess, [
      "school_players.delete",
      "school_players.manage",
      "players.manage",
    ]);

  const deleteMutation = useMutation({
    mutationFn: () => workspaceApi.schoolPlayers.remove(schoolId, id),
    onSuccess: async () => {
      toast.success("Player removed.");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["workspace-school-players", schoolId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["school-player-profile-school-player", schoolId, id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["school-player-profile-athlete", id],
        }),
      ]);
      history.push(backTo);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to remove this player."));
    },
  });

  const setTab = useCallback(
    (tab: PlayerProfileTab) => {
      history.replace({
        pathname: location.pathname,
        search: tab === "overview" ? "" : `?tab=${tab}`,
        state: location.state,
      });
    },
    [history, location.pathname, location.state],
  );

  const headerActions = useMemo(
    () => (
      <>
        <SchoolPlayerProfileSecondaryButton onClick={() => history.push(backTo)} type="button">
          <FaArrowLeft size={12} />
          Back
        </SchoolPlayerProfileSecondaryButton>
        {canDeletePlayer ? (
          <SchoolPlayerProfileDangerButton
            onClick={() => {
              if (window.confirm("Delete this player? This action cannot be undone.")) {
                deleteMutation.mutate();
              }
            }}
            type="button"
          >
            <FaTrash size={12} />
            Delete
          </SchoolPlayerProfileDangerButton>
        ) : null}
      </>
    ),
    [backTo, canDeletePlayer, deleteMutation, history],
  );

  const headerTabs = useMemo(
    () =>
      hasProfile ? (
        <SchoolHubTabRow>
          {PLAYER_PROFILE_TABS.map((tab) => (
            <SchoolHubTabButton
              $active={activeTab === tab.id}
              key={tab.id}
              onClick={() => setTab(tab.id)}
              type="button"
            >
              {tab.label}
            </SchoolHubTabButton>
          ))}
        </SchoolHubTabRow>
      ) : undefined,
    [activeTab, hasProfile, setTab],
  );

  const tabProps = {
    athlete,
    athleteLoading,
    linkedSports,
    linkedTeams,
    playerId: id,
    schoolId,
    schoolPlayer,
  };

  return (
    <AppLayout route="/workspace/school-players">
      <SchoolOpsHeader actions={headerActions} tabs={headerTabs} title={profileTitle} />

      <SchoolHubContentStack>
        {isLoading ? loadingSkeleton : null}

        {!isLoading && !hasProfile ? (
          <SchoolPlayerProfileSection>
            <EmptyState
              description="This player could not be found inside the selected school scope."
              title="Player not found"
            />
          </SchoolPlayerProfileSection>
        ) : null}

        {hasProfile && activeTab === "overview" ? (
          <SchoolPlayerOverviewTab {...tabProps} />
        ) : null}
        {hasProfile && activeTab === "schedule" ? (
          <SchoolPlayerScheduleTab {...tabProps} />
        ) : null}
        {hasProfile && activeTab === "performance" ? (
          <SchoolPlayerPerformanceTab {...tabProps} />
        ) : null}
        {hasProfile && activeTab === "media" ? <SchoolPlayerMediaTab {...tabProps} /> : null}
        {hasProfile && activeTab === "documents" ? (
          <SchoolPlayerDocumentsTab {...tabProps} />
        ) : null}
      </SchoolHubContentStack>
    </AppLayout>
  );
};

export default SchoolPlayerProfilePage;
